import { colors } from "../../website-kit";
import Navigation from "@/components/samples/Navigation";
import DarkModeToggle from "@/components/samples/DarkModeToggle";
import withStagingOnly, {
  getServerSideProps,
} from "@/components/samples/withStagingOnly";
import { useEffect, useState } from "react";

function groupColorsByFamily(palette: Record<string, string>) {
  return Object.entries(palette).reduce<Record<string, [string, string][]>>(
    (acc, [name, value]) => {
      const family = name.split("-")[0] || "other";
      if (!acc[family]) {
        acc[family] = [];
      }
      acc[family]?.push([name, value]);
      return acc;
    },
    {}
  );
}

const ColorSwatch: React.FC<{ name: string; value: string }> = ({
  name,
  value,
}) => {
  const [hexValue, setHexValue] = useState<string>("");

  useEffect(() => {
    const hex = getComputedStyle(document.documentElement)
      .getPropertyValue(`--color-${name}`)
      .toLocaleUpperCase()
      .trim();
    setHexValue(hex);
  }, [name]);

  return (
    <div className="flex flex-col items-center rounded border border-wk-primary-ui bg-wk-primary-background p-4">
      <div className={`mb-2 h-12 w-12 rounded shadow bg-${name}`} />
      <div className="wk-text-xs font-mono text-wk-primary-text">{name}</div>
      <div className="wk-text-xs text-wk-primary-text">{value}</div>
      <div className="wk-text-xs text-wk-primary-text">{hexValue}</div>
    </div>
  );
};

function PalettePage() {
  const grouped = groupColorsByFamily(colors.palette);

  return (
    <div className="min-h-screen bg-wk-primary-background">
      <Navigation />
      <DarkModeToggle />
      <main className="mx-auto max-w-7xl p-8">
        <header className="mb-12">
          <h1 className="wk-h4 mb-4 font-bold text-wk-primary-text">
            Color Palette
          </h1>
          <p className="wk-text-lg max-w-3xl text-wk-primary-text">
            Our color palette is designed for accessibility and flexibility,
            supporting both light and dark modes. All colors are available as
            CSS variables and Tailwind classes.
          </p>
        </header>
        <section>
          {Object.entries(grouped).map(([family, swatches]) => (
            <div key={family} className="mb-8">
              <h2 className="wk-h6 mb-4 font-semibold text-wk-primary-text">
                {family}
              </h2>
              <div className="grid gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {swatches.map(([name, value]) => (
                  <ColorSwatch key={name} name={name} value={value} />
                ))}
              </div>
            </div>
          ))}
        </section>
      </main>
    </div>
  );
}

export { getServerSideProps };
export default withStagingOnly(PalettePage);
